import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useConvertedRows } from '../hooks/useConvertedRows';
import { useDisplayCurrency } from './DisplayCurrencyContext';
import { mergeRateSources } from '../utils/rateSources';

/**
 * MI-318 — the converted value of each movement row on screen.
 *
 * The flow is inverted on purpose: a row **registers** the id it needs and the
 * provider answers, rather than the list passing a map downwards. Two reasons.
 * The amount renders five levels below the list (list → day article → desktop /
 * mobile list → row → amount), so props would touch components that have
 * nothing to do with currency. And in the grouped view the rows do not exist at
 * the top at all — each group hydrates its own window as it is expanded, so
 * there is no single place that knows what is visible.
 *
 * Registrations are batched (one frame's worth) into a single request, and the
 * hook below only ever asks for ids it has not already resolved.
 */
const ConvertedAmountsContext = createContext({
  amounts: null,
  sources: {},
  resolved: null,
  displayCurrency: null,
  register: () => {},
});

/** How long to collect ids before asking. Long enough for a page of rows to
 *  mount, short enough to be invisible. */
const BATCH_MS = 50;

export function ConvertedAmountsProvider({ children }) {
  const { displayCurrency } = useDisplayCurrency();
  const [ids, setIds] = useState([]);
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);

  const register = useCallback((id) => {
    if (id == null) return;
    pendingRef.current.add(id);
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const batch = pendingRef.current;
      pendingRef.current = new Set();
      setIds((prev) => {
        const merged = new Set(prev);
        let added = false;
        batch.forEach((v) => { if (!merged.has(v)) { merged.add(v); added = true; } });
        return added ? [...merged] : prev;
      });
    }, BATCH_MS);
  }, []);

  // The handle goes with the timer: left set, every later `register` would
  // take it for a batch on its way and never send one. StrictMode unmounts a
  // provider once on mount, so a list whose rows mount with it stalled so.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const { amounts, sources, resolved } = useConvertedRows(ids, {
    enabled: Boolean(displayCurrency),
  });

  const value = useMemo(
    () => ({ amounts, sources, resolved, displayCurrency, register }),
    [amounts, sources, resolved, displayCurrency, register],
  );
  return (
    <ConvertedAmountsContext.Provider value={value}>
      {children}
    </ConvertedAmountsContext.Provider>
  );
}

/**
 * MI-315 — the sum of a selection, and how many movements make it, for the
 * Sum and Average a spreadsheet shows beside a selection.
 *
 * Each movement counts at the figure its row shows, signed the way the day
 * headers sign a total: income adds, an expense subtracts, a transfer adds
 * nothing — a selection of transfers alone sums to zero. `count` is the income
 * and expenses behind `total`, so an average of total / count is not diluted by
 * transfers. An ignored movement is left out, as every total leaves it out.
 * One entry per currency, because a movement the server could not convert
 * keeps its own. `rateSources` is every rate the converted entry read (MI-329).
 */
export function useSelectionTotals(movements) {
  const { amounts, sources, resolved, displayCurrency } = useContext(ConvertedAmountsContext);

  return useMemo(() => {
    const byCurrency = new Map();
    let isPending = false;

    movements.forEach((m) => {
      if (m.is_ignored) return;
      const sign = m.type === 'Income' ? 1 : m.type === 'Expenses' ? -1 : 0;
      const foreign = Boolean(displayCurrency) && m.currency !== displayCurrency;
      const converted = foreign && amounts ? amounts[m.id] : undefined;
      if (foreign && !resolved?.has(m.id)) isPending = true;
      const currency = converted != null ? displayCurrency : m.currency;
      const entry = byCurrency.get(currency)
        || { currency, total: 0, count: 0, isConverted: false, rateSources: [] };
      if (sign !== 0) {
        entry.total += sign * (converted != null ? converted : m.amount || 0);
        entry.count += 1;
        entry.isConverted = entry.isConverted || converted != null;
        if (converted != null) entry.rateSources = mergeRateSources(entry.rateSources, sources?.[m.id]);
      }
      byCurrency.set(currency, entry);
    });

    return { totals: [...byCurrency.values()], isPending };
  }, [movements, amounts, sources, resolved, displayCurrency]);
}

/**
 * What a row should display for `movement`.
 *
 * Returns the original amount and currency unless a converted value exists, so
 * every call site renders the result unconditionally. `isConverted` is what
 * tells the row to prefix the `≈` marking the figure as approximate, and
 * `rateSources` the rates that figure read (MI-329). A movement
 * the server could not convert simply never appears in the map — it is not an
 * error, and the "N not converted" note above the list already says why.
 */
export function useDisplayAmount(movement) {
  const { amounts, sources, resolved, displayCurrency, register } = useContext(ConvertedAmountsContext);
  const id = movement?.id;

  useEffect(() => {
    if (displayCurrency && id != null) register(id);
  }, [displayCurrency, id, register]);

  const converted = amounts && id != null ? amounts[id] : undefined;
  const sameCurrency = movement?.currency === displayCurrency;
  // Rows arrive before their converted values do — two requests — so for a
  // moment a row would show a figure in the old currency that is about to
  // change: a number a reader can act on before it settles. Callers dim the
  // amount while this is true. An id the server has already answered about is
  // settled even when the answer was "cannot convert", which is why `resolved`
  // exists: otherwise an unconvertible row would stay dimmed forever.
  const isPending = Boolean(
    displayCurrency && !sameCurrency && id != null && !resolved?.has(id),
  );

  if (converted == null || !displayCurrency) {
    return {
      amount: movement?.amount,
      currency: movement?.currency,
      isConverted: false,
      isPending,
    };
  }
  // A movement already in the display currency was not converted — it was
  // multiplied by one. Marking it approximate, or showing "≈ COP 5,200" under
  // "COP 5200", claims a transformation that never happened.
  if (sameCurrency) {
    return {
      amount: movement.amount,
      currency: movement.currency,
      isConverted: false,
      isPending: false,
    };
  }
  return {
    amount: converted,
    currency: displayCurrency,
    isConverted: true,
    isPending: false,
    rateSources: sources?.[id] || [],
  };
}
